import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import clsx from "clsx";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { api, ApiError } from "@/lib/apiClient";
import {
  createMaintenanceCampaign,
  listMaintenanceTypes,
  type MaintenanceType,
} from "@/lib/wartungen";

type DateMode = "day" | "month" | "year";

interface ProjectRow {
  project_id: number;
  auftragsnummer?: string;
  beschreibung?: string;
  customer_name?: string;
  location_name?: string;
  hero_address_id?: number | null;
  hero_customer_id?: number | null;
}

interface ContractRow {
  contract_id: number;
  contract_number?: string;
  name?: string;
  status?: string;
  location_count?: number;
  project_hero_address_id?: number | null;
  locations?: { hero_address_id?: number; location_name?: string; building_name?: string }[];
  [k: string]: unknown;
}

interface ContractMeta {
  hero_customer_id?: number | null;
  hero_address_id?: number | null;
  maintenance_types?: MaintenanceType[];
  locations?: { hero_address_id?: number; location_name?: string; building_name?: string }[];
  [k: string]: unknown;
}

function validateDate(mode: string, value: string): boolean {
  const m = String(mode || "").trim();
  const v = String(value || "").trim();
  if (!m || !v) return false;
  if (m === "day") return /^\d{4}-\d{2}-\d{2}$/.test(v);
  if (m === "month") return /^\d{4}-\d{2}$/.test(v);
  if (m === "year") return /^\d{4}$/.test(v) && Number(v) >= 1900 && Number(v) <= 9999;
  return false;
}

function locationName(loc: { location_name?: string; building_name?: string } | undefined | null): string {
  if (!loc) return "";
  return String(loc.location_name || loc.building_name || "").trim();
}

function contractLabel(c: ContractRow | ContractMeta | null): string {
  if (!c) return "Kein Vertrag geladen";
  const row = c as ContractRow;
  const label = [row.contract_number, row.name, row.status].filter(Boolean).join(" – ");
  return label || `Vertrag ${row.contract_id}`;
}

export function ProjektStartPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const typeKeyParam = (params.get("type_key") || params.get("type") || params.get("kategorie") || "").trim();
  const contractIdParam = (() => {
    const n = Number((params.get("contract_id") || "").trim());
    return Number.isFinite(n) && n > 0 ? n : null;
  })();

  const [maintenanceTypes, setMaintenanceTypes] = useState<MaintenanceType[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [projectsHint, setProjectsHint] = useState("Suche starten, um Projekte zu sehen.");
  const [selectedProject, setSelectedProject] = useState<ProjectRow | null>(null);

  const [contracts, setContracts] = useState<ContractRow[]>([]);
  const [selectedContract, setSelectedContract] = useState<ContractRow | null>(null);
  const [contractMeta, setContractMeta] = useState<ContractMeta | null>(null);

  const [selectedTypeKey, setSelectedTypeKey] = useState(typeKeyParam);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [dateMode, setDateMode] = useState<DateMode>("day");
  const [dateValue, setDateValue] = useState("");

  const [feedback, setFeedback] = useState<{ tone: "info" | "error" | "warning"; message: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [title, setTitle] = useState("Projekt auswählen");

  const contractMode = contractIdParam != null;

  useEffect(() => {
    void (async () => {
      try {
        const items = await listMaintenanceTypes();
        setMaintenanceTypes(items);
        if (typeKeyParam) {
          const t = items.find((x) => String(x.key).trim() === typeKeyParam);
          setTitle(`Projekt auswählen – ${t?.name || typeKeyParam}`);
        } else if (contractIdParam) {
          setTitle(`Projekt auswählen – Vertrag ${contractIdParam}`);
        }
      } catch {
        setMaintenanceTypes([]);
      }
    })();
  }, [typeKeyParam, contractIdParam]);

  const resolveHeroAddressId = useCallback((): number | null => {
    const candidate = Number(
      selectedLocationId ??
        (selectedContract as { project_hero_address_id?: number } | null)?.project_hero_address_id ??
        selectedProject?.hero_address_id ??
        contractMeta?.hero_address_id,
    );
    return Number.isFinite(candidate) && candidate > 0 ? candidate : null;
  }, [selectedLocationId, selectedContract, selectedProject, contractMeta]);

  const loadContractsForProject = useCallback(
    async (project: ProjectRow, typeKey: string) => {
      if (!project.project_id || !typeKey) return;
      try {
        const usp = new URLSearchParams({ project_id: String(project.project_id), type_key: typeKey });
        const payload = await api.get<{ project?: ProjectRow; contracts?: ContractRow[] }>(
          `/maintenance/contracts?${usp.toString()}`,
        );
        const filtered = (payload.contracts || []).filter((c) =>
          contractMode ? Number(c.contract_id) === contractIdParam : true,
        );
        setContracts(filtered);
        if (contractMode) {
          const match = filtered.find((c) => Number(c.contract_id) === contractIdParam) || filtered[0] || null;
          setSelectedContract(match);
        } else if (filtered.length === 1) {
          setSelectedContract(filtered[0] ?? null);
        }
      } catch {
        setContracts([]);
        setSelectedContract(null);
        setFeedback({ tone: "error", message: "Verträge konnten nicht geladen werden." });
      }
    },
    [contractMode, contractIdParam],
  );

  const runSearch = useCallback(
    async (query: string) => {
      const q = query.trim();
      if (contractMode) {
        try {
          const usp = new URLSearchParams({ limit: "20" });
          if (q) usp.set("q", q);
          if (selectedLocationId) usp.set("hero_address_id", String(selectedLocationId));
          const payload = await api.get<{ contract?: ContractMeta; projects?: ProjectRow[] }>(
            `/maintenance/contracts/${encodeURIComponent(String(contractIdParam))}/projects?${usp.toString()}`,
          );
          setContractMeta(payload.contract || null);
          const locs = payload.contract?.locations || [];
          if (locs.length > 1 && !selectedLocationId) {
            setProjects([]);
            setProjectsHint("Bitte zuerst einen Vertrags-Ort auswählen.");
            return;
          }
          const rows = payload.projects || [];
          setProjects(rows);
          setProjectsHint(rows.length ? "" : "Keine passenden Projekte gefunden.");
        } catch {
          setProjects([]);
          setProjectsHint("Projektsuche fehlgeschlagen.");
          setFeedback({ tone: "error", message: "Projekte konnten nicht geladen werden." });
        }
        return;
      }

      if (q.length < 2) {
        setProjects([]);
        setSelectedProject(null);
        setProjectsHint("Bitte mindestens 2 Zeichen eingeben.");
        return;
      }
      try {
        const rows = await api.get<ProjectRow[]>(
          `/maintenance/projects/search?q=${encodeURIComponent(q)}&limit=20`,
        );
        const list = Array.isArray(rows) ? rows : [];
        setProjects(list);
        setProjectsHint(list.length ? "" : "Keine passenden Projekte gefunden.");
      } catch {
        setProjects([]);
        setProjectsHint("Projektsuche fehlgeschlagen.");
        setFeedback({ tone: "error", message: "Projekte konnten nicht geladen werden." });
      }
    },
    [contractMode, contractIdParam, selectedLocationId],
  );

  // Initial load for contract mode
  const initialised = useRef(false);
  useEffect(() => {
    if (initialised.current) return;
    initialised.current = true;
    if (!typeKeyParam && !contractIdParam) {
      setProjectsHint("Ohne Wartungskategorie oder Vertrag ist kein Start möglich.");
      setFeedback({ tone: "warning", message: "Bitte Seite aus einer Kategorie oder einem Vertrag öffnen." });
      return;
    }
    if (contractMode) void runSearch("");
  }, [typeKeyParam, contractIdParam, contractMode, runSearch]);

  // Debounced search on query change (non-contract mode)
  useEffect(() => {
    if (contractMode) return;
    const handle = window.setTimeout(() => void runSearch(searchQuery), 250);
    return () => window.clearTimeout(handle);
  }, [searchQuery, contractMode, runSearch]);

  const availableTypes = useMemo(() => {
    if (contractMode) return contractMeta?.maintenance_types || [];
    return maintenanceTypes;
  }, [contractMode, contractMeta, maintenanceTypes]);

  const showTypeSelect = availableTypes.length > 1;

  const contractLocations = useMemo(
    () => (Array.isArray(contractMeta?.locations) ? contractMeta!.locations! : []),
    [contractMeta],
  );
  const showLocationFilter = contractMode && contractLocations.length > 1;

  function selectProject(project: ProjectRow) {
    const enriched: ProjectRow = {
      ...project,
      hero_customer_id: contractMeta?.hero_customer_id != null ? Number(contractMeta.hero_customer_id) : project.hero_customer_id ?? null,
      hero_address_id: contractMeta?.hero_address_id != null ? Number(contractMeta.hero_address_id) : project.hero_address_id ?? null,
    };
    setSelectedProject(enriched);
    setSelectedContract(null);
    setContracts([]);
    setFeedback(null);
    if (selectedTypeKey) void loadContractsForProject(enriched, selectedTypeKey);
  }

  const canContinue =
    !submitting &&
    Boolean(selectedTypeKey) &&
    Boolean(selectedProject?.project_id) &&
    Boolean(selectedContract?.contract_id) &&
    validateDate(dateMode, dateValue);

  async function handleContinue() {
    if (!selectedProject || !selectedContract || !selectedTypeKey || !validateDate(dateMode, dateValue)) {
      setFeedback({ tone: "warning", message: "Bitte Projekt, Vertrag, Typ und Wartungsdatum gültig auswählen." });
      return;
    }
    setSubmitting(true);
    setFeedback(null);
    try {
      const type = maintenanceTypes.find((t) => String(t.key).trim() === selectedTypeKey.trim());
      const maintenanceTypeId = Number(type?.id);
      if (!Number.isFinite(maintenanceTypeId) || maintenanceTypeId <= 0) {
        throw new Error("Wartungstyp konnte nicht aufgelöst werden.");
      }
      const heroAddressId = resolveHeroAddressId();
      const result = await createMaintenanceCampaign({
        maintenance_type_id: maintenanceTypeId,
        project_id: Number(selectedProject.project_id),
        contract_id: Number(selectedContract.contract_id),
        name: String(selectedProject.beschreibung || `Wartung ${dateValue}`).trim(),
        date_mode: dateMode,
        date_value: dateValue.trim(),
        hero_address_id: heroAddressId,
      });
      const campaignId = Number(result?.campaign?.id);
      if (!Number.isFinite(campaignId) || campaignId <= 0) {
        throw new Error("Wartungshülle konnte nicht erstellt werden.");
      }
      const usp = new URLSearchParams();
      usp.set("type_key", selectedTypeKey);
      usp.set("project_id", String(selectedProject.project_id));
      usp.set("contract_id", String(selectedContract.contract_id));
      usp.set("campaign_id", String(campaignId));
      usp.set("date_mode", dateMode);
      usp.set("date_value", dateValue);
      if (heroAddressId) usp.set("hero_address_id", String(heroAddressId));
      if (selectedProject.beschreibung) usp.set("maintenance_title", String(selectedProject.beschreibung));
      if (selectedProject.location_name) usp.set("base_location_name", String(selectedProject.location_name));
      navigate(`/wartungen/huelle-detail?${usp.toString()}`);
    } catch (err) {
      setFeedback({
        tone: "error",
        message: err instanceof ApiError ? err.message : "Wartungshülle konnte nicht erstellt werden.",
      });
      setSubmitting(false);
    }
  }

  return (
    <AppShell
      title={title}
      subtitle={
        contractMode ? `Aktiver Vertrag: ${contractLabel(selectedContract)}` : typeKeyParam ? `Wartungstyp: ${typeKeyParam}` : undefined
      }
      back={{
        to: typeKeyParam
          ? `/wartungen/kategorie?kategorie=${encodeURIComponent(typeKeyParam)}`
          : contractIdParam
            ? `/wartungen/vertrags-wartungen?contract_id=${encodeURIComponent(String(contractIdParam))}`
            : "/wartungen?tab=vertraege",
        label: "zurück",
      }}
    >
      {feedback ? (
        <Alert variant={feedback.tone === "error" ? "error" : feedback.tone === "warning" ? "warning" : "info"} className="mb-4">
          {feedback.message}
        </Alert>
      ) : null}

      {showLocationFilter ? (
        <div className="mb-4 rounded-ios border border-separator bg-card p-4 shadow-ios">
          <label className="space-y-1">
            <span className="eh-label text-xs">Vertrags-Ort</span>
            <select
              className="eh-input w-full max-w-md"
              value={selectedLocationId ? String(selectedLocationId) : ""}
              onChange={(e) => {
                const n = Number(e.target.value);
                setSelectedLocationId(Number.isFinite(n) && n > 0 ? n : null);
                setSelectedProject(null);
                setSelectedContract(null);
                setContracts([]);
              }}
            >
              <option value="">Ort auswählen</option>
              {contractLocations.map((loc) => {
                const id = Number(loc.hero_address_id);
                return (
                  <option key={id} value={id}>
                    {locationName(loc) || `Hero-Adresse ${id}`} (Hero-Adresse {id})
                  </option>
                );
              })}
            </select>
          </label>
          <p className="mt-1 text-xs text-label-secondary">
            Bitte zuerst den Vertrags-Ort wählen; danach werden nur passende Projekte angezeigt.
          </p>
        </div>
      ) : null}

      <section className="rounded-ios border border-separator bg-card p-4 shadow-ios">
        <h3 className="mb-3 text-base font-semibold text-label">1. Projekt auswählen</h3>
        {!contractMode ? (
          <input
            className="eh-input mb-3 w-full max-w-md"
            placeholder="Projekt suchen (Auftragsnummer, Beschreibung …)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        ) : null}
        <div className="overflow-x-auto rounded-lg border border-separator">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-separator text-left text-xs uppercase tracking-wide text-label-tertiary">
                <th className="px-3 py-2"></th>
                <th className="px-3 py-2 font-medium">Auftrag</th>
                <th className="px-3 py-2 font-medium">Beschreibung</th>
                <th className="px-3 py-2 font-medium">Kunde</th>
                <th className="px-3 py-2 font-medium">Ort</th>
              </tr>
            </thead>
            <tbody>
              {projects.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-3 py-3 text-label-secondary">
                    {projectsHint}
                  </td>
                </tr>
              ) : (
                projects.map((p) => (
                  <tr
                    key={p.project_id}
                    className={clsx(
                      "cursor-pointer border-b border-separator/60 hover:bg-fill-tertiary",
                      selectedProject?.project_id === p.project_id && "bg-accent-soft",
                    )}
                    onClick={() => selectProject(p)}
                  >
                    <td className="px-3 py-2">
                      <input
                        type="radio"
                        name="project-selection"
                        checked={selectedProject?.project_id === p.project_id}
                        onChange={() => selectProject(p)}
                      />
                    </td>
                    <td className="px-3 py-2">{p.auftragsnummer || "—"}</td>
                    <td className="px-3 py-2">{p.beschreibung || "—"}</td>
                    <td className="px-3 py-2">{p.customer_name || "—"}</td>
                    <td className="px-3 py-2">{p.location_name || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-4 rounded-ios border border-separator bg-card p-4 shadow-ios">
        <h3 className="mb-3 text-base font-semibold text-label">2. Vertrag &amp; Typ</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          {!contractMode ? (
            <label className="space-y-1">
              <span className="eh-label text-xs">Vertrag</span>
              <select
                className="eh-input w-full"
                disabled={!contracts.length}
                value={selectedContract ? String(selectedContract.contract_id) : ""}
                onChange={(e) => {
                  const id = Number(e.target.value);
                  setSelectedContract(contracts.find((c) => Number(c.contract_id) === id) || null);
                }}
              >
                <option value="">{contracts.length ? "Vertrag auswählen" : "Bitte zuerst Projekt wählen"}</option>
                {contracts.map((c) => (
                  <option key={c.contract_id} value={c.contract_id}>
                    {contractLabel(c)}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <div className="space-y-1">
              <span className="eh-label text-xs">Aktiver Vertrag</span>
              <p className="eh-input flex items-center bg-fill-tertiary">{contractLabel(selectedContract)}</p>
            </div>
          )}

          {showTypeSelect ? (
            <label className="space-y-1">
              <span className="eh-label text-xs">Wartungstyp</span>
              <select
                className="eh-input w-full"
                value={selectedTypeKey}
                onChange={(e) => {
                  setSelectedTypeKey(e.target.value);
                  setSelectedContract(null);
                  setContracts([]);
                  if (selectedProject && e.target.value) void loadContractsForProject(selectedProject, e.target.value);
                }}
              >
                <option value="">Typ auswählen</option>
                {availableTypes.map((t) => (
                  <option key={String(t.key)} value={String(t.key)}>
                    {t.name || t.key}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      </section>

      <section className="mt-4 rounded-ios border border-separator bg-card p-4 shadow-ios">
        <h3 className="mb-3 text-base font-semibold text-label">3. Wartungsdatum</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="space-y-1">
            <span className="eh-label text-xs">Modus</span>
            <select
              className="eh-input w-full"
              value={dateMode}
              onChange={(e) => {
                setDateMode(e.target.value as DateMode);
                setDateValue("");
              }}
            >
              <option value="day">Tag</option>
              <option value="month">Monat</option>
              <option value="year">Jahr</option>
            </select>
          </label>
          <label className="space-y-1">
            <span className="eh-label text-xs">Datum</span>
            {dateMode === "month" ? (
              <input type="month" className="eh-input w-full" value={dateValue} onChange={(e) => setDateValue(e.target.value)} />
            ) : dateMode === "year" ? (
              <input
                type="number"
                min={1900}
                max={9999}
                className="eh-input w-full"
                placeholder="YYYY"
                value={dateValue}
                onChange={(e) => setDateValue(e.target.value)}
              />
            ) : (
              <input type="date" className="eh-input w-full" value={dateValue} onChange={(e) => setDateValue(e.target.value)} />
            )}
          </label>
        </div>
      </section>

      <div className="mt-4">
        <Button type="button" disabled={!canContinue} onClick={handleContinue}>
          {submitting ? "Erstelle Wartungshülle …" : "Weiter zur Wartungshülle"}
        </Button>
      </div>
    </AppShell>
  );
}
